import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const BlogList = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Synchronize data stream loop on component mount
  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const response = await fetch('/api/blogs/list');
        const result = await response.json();
        
        if (response.ok && result.success) {
          setBlogs(result.data);
        } else {
          setError(result.message || 'Failed to pull dynamic blog index.');
        }
      } catch (err) {
        console.error('Network Data Stream Failure:', err);
        setError('Could not establish connection link with backend API node.');
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  return (
    <div className="bg-slate-950 text-slate-100 min-h-screen py-16 font-sans relative z-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* DASHBOARD HEADER */}
        <div className="border-b border-slate-900 pb-6 mb-12 flex justify-between items-end">
          <div>
            <h1 className="text-3xl font-black text-white tracking-tight">MoreView Articles</h1>
            <p className="text-sm text-slate-400 mt-1">
              Explore dynamic publication entries synchronized directly from your database ledger.
            </p>
          </div>
          <span className="text-xs font-mono text-indigo-400 bg-indigo-950/40 border border-indigo-900/60 px-3 py-1.5 rounded-xl">
            Total Posts: {blogs.length}
          </span>
        </div>

        {/* LOADING STATE SCREEN */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-32 gap-3 text-xs font-mono text-slate-500">
            <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
            <span>Polling data channel... reading MySQL row clusters</span>
          </div>
        )}

        {/* ERROR STATE VIEWPORT */}
        {error && (
          <div className="bg-red-950/30 border border-red-900 text-red-400 text-xs font-mono p-5 rounded-2xl text-center max-w-md mx-auto shadow-2xl">
            <div className="text-lg mb-1">⚠️</div>
            <p className="font-bold mb-1">Data Pipeline Interruption</p>
            <p className="text-slate-400 text-[11px]">{error}</p>
          </div>
        )}

        {/* INTERACTIVE DATA GRID MATRIX */}
        {!loading && !error && (
          blogs.length === 0 ? (
            <div className="text-center py-24 bg-slate-900/10 border border-dashed border-slate-900 rounded-2xl">
              <div className="text-2xl mb-2">📁</div>
              <p className="text-slate-400 text-sm">No articles discovered in the relational table schema.</p>
              <p className="text-xs text-slate-600 mt-1">Launch the admin workspace node to insert content packages.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogs.map((blog) => (
                <article 
                  key={blog.id} 
                  className="bg-slate-900/40 border border-slate-900 rounded-2xl overflow-hidden hover:border-slate-800 hover:shadow-2xl hover:shadow-indigo-950/20 transition-all duration-300 flex flex-col group h-full"
                >
                  {/* FEATURED BANNER THUMBNAIL */}
                  <div className="relative aspect-[16/9] w-full bg-slate-950 overflow-hidden border-b border-slate-950">
                    {blog.top_image ? (
                      <img 
                        src={`${blog.top_image}`} 
                        alt={blog.title} 
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center gap-1 text-[10px] font-mono text-slate-600 bg-slate-950">
                        <span>🖼️</span>
                        <span>NO IMAGE LINKED</span>
                      </div>
                    )}
                    
                    {/* DYNAMIC CATEGORY METADATA BADGE */}
                    <span className="absolute top-3 left-3 bg-indigo-600/90 backdrop-blur text-white text-[10px] font-extrabold uppercase tracking-widest px-2.5 py-1 rounded-md shadow-md border border-indigo-500/30">
                      {blog.tag}
                    </span>
                  </div>

                  {/* CONTENT INTERFACE BODY */}
                  <div className="p-6 flex flex-col flex-grow">
                    <div className="flex items-center gap-2 text-[10px] font-mono text-slate-500 mb-3">
                      <span>📅</span>
                      <span>
                        {new Date(blog.created_at).toLocaleDateString('en-US', { 
                          day: 'numeric', 
                          month: 'short', 
                          year: 'numeric' 
                        })}
                      </span>
                    </div>
                    
                    <h2 className="text-base font-bold text-white tracking-tight group-hover:text-indigo-400 transition-colors line-clamp-2 mb-3 leading-snug">
                      {blog.title}
                    </h2>
                    
                    {/* URL PERMALINK ROUTE CARD */}
                    <div className="mt-auto pt-4 border-t border-slate-900/60 flex flex-col gap-3">
                      <div className="flex items-center gap-1.5 text-xs text-slate-400 font-mono bg-slate-950/80 border border-slate-900 px-3 py-1.5 rounded-xl w-full overflow-hidden text-ellipsis whitespace-nowrap">
                        <span className="text-slate-600">slug:</span>
                        <span className="text-indigo-400">/{blog.slug}</span>
                      </div>

                      
			
<Link 
  to={`/blog/${blog.slug}`}
  className="w-full block text-center bg-slate-900 hover:bg-indigo-600 text-slate-200 hover:text-white font-bold text-xs py-3 rounded-xl border border-slate-800 hover:border-indigo-500 transition-all shadow-md"
>
  Read Article View →
</Link>
                    </div>
                  </div>

                </article>
              ))}
            </div>
          )
        )}

      </div>
    </div>
  );
};

export default BlogList;
